import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2 } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";

import { Package } from '../types';
import { usePackagesApi } from '../apis/packages-apis';

export const PackagesList: React.FC = () => {
  const initialLoadRef = useRef(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [displayedPackages, setDisplayedPackages] = useState<Package[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Package[] | null>(null);
  
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  const packagesApi = usePackagesApi();

  const { addToast } = useToast();
  const navigate = useNavigate();
  const ITEMS_PER_PAGE = 20;

  const updateDisplayedPackages = useCallback((currentPage: number, packagesArray: Package[]) => {
    const startIndex = 0;
    const endIndex = currentPage * ITEMS_PER_PAGE;
    setDisplayedPackages(packagesArray.slice(startIndex, endIndex));
    setPage(currentPage);
  }, []);

  const loadMorePackages = useCallback(() => {
    if (!searchTerm && !loading) {
      const nextPage = page + 1;
      updateDisplayedPackages(nextPage, packages);
    }
  }, [searchTerm, loading, page, packages, updateDisplayedPackages]);

  const fetchPackages = useCallback(async () => {
    if (loading || initialLoadRef.current) return;
    
    setLoading(true);
    try {
      initialLoadRef.current = true;
      const getAllPackages = false;
      const fetchedPackages = await packagesApi.getPackages(getAllPackages);
      setPackages(fetchedPackages);
      setTotalCount(fetchedPackages.length);
      updateDisplayedPackages(1, fetchedPackages);
    } catch (error) {
      console.error('Error fetching packages:', error);
      addToast({
        title: "Error",
        description: "Failed to retrieve packages. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [loading, updateDisplayedPackages, addToast]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = packages.filter(pkg =>
        pkg.packageName.toLowerCase().includes(term.toLowerCase()) ||
        pkg.packageProductName.toLowerCase().includes(term.toLowerCase()) ||
        pkg.packageDescription?.toLowerCase().includes(term.toLowerCase()) ||
        pkg.packageTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      updateDisplayedPackages(1, packages);
    }
  }, 300);

  const confirmDelete = (id: string) => {
    setPackageToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (packageToDelete) {
      setIsDeletingId(packageToDelete);
      try {
        await packagesApi.deletePackage(packageToDelete);
        addToast({
          title: "Success",
          description: "Package successfully deleted",
          variant: "default",
        });
        initialLoadRef.current = false; // Reset the ref to allow a new fetch
        fetchPackages();
      } catch (error) {
        addToast({
          title: "Error",
          description: "Failed to delete package. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsDeletingId(null);
        setDeleteDialogOpen(false);
        setPackageToDelete(null);
      }
    }
  };

  const handleView = (pkg: Package) => {
    navigate(`/marketplace/packages/edit/${pkg.packageId}`);
  };

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);

      if (!searchTerm && !loading) {
        const scrolledToBottom =
          window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 800;

        if (scrolledToBottom) {
          loadMorePackages();
        }
      }
    }, 200);

    if (initialLoadRef.current) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [searchTerm, loading, loadMorePackages]);

  const currentPackages = searchResults || displayedPackages;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl mb-4">
        Packages Management
      </h1>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-muted-foreground" size={20} />
          <Input
            type="text"
            placeholder="Search packages..."
            className="w-full pl-10"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      <Button
        variant="default"
        className="mb-4 bg-green-500 hover:bg-green-600 text-white"
        onClick={() => navigate('/marketplace/packages/add')}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add New Package
      </Button>

      <div className="text-sm text-muted-foreground mb-4">
        {searchTerm ? (
          `Found ${searchResults?.length || 0} results for "${searchTerm}"`
        ) : (
          `Showing ${displayedPackages.length} of ${totalCount} packages`
        )}
      </div>

      <div className="grid gap-4">
        {currentPackages.map((pkg) => (
          <Card key={pkg.packageId} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <h2 className="text-xl font-semibold">{pkg.packageName}</h2>
                  <p className="text-muted-foreground mt-2 line-clamp-2">{pkg.packageDescription}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="inline-flex items-center rounded-md bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 whitespace-nowrap">
                      {pkg.packageProductName}
                    </span>
                    {pkg.packageTags && pkg.packageTags.length > 0 && (
                      <>
                        {pkg.packageTags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20"
                          >
                            {tag}
                          </span>
                        ))}
                        {pkg.packageTags.length > 3 && (
                          <div className="relative inline-block group">
                            <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20 cursor-pointer">
                              +{pkg.packageTags.length - 3}
                            </span>
                            <div className="absolute left-0 mt-2 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-10 hidden group-hover:block">
                              {pkg.packageTags.slice(3).map((tag, index) => (
                                <span
                                  key={index}
                                  className="block text-xs text-gray-700"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 ml-4">
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleView(pkg)}
                      className="bg-blue-100 hover:bg-blue-200 text-blue-600"
                      disabled={isDeletingId === pkg.packageId}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => confirmDelete(pkg.packageId)}
                      className="bg-red-500 hover:bg-red-600 text-white"
                      disabled={isDeletingId === pkg.packageId}
                    >
                      {isDeletingId === pkg.packageId ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col items-center my-8 gap-2">
          <Spinner className="border-blue-500" />
          <p className="text-center text-muted-foreground">Loading Packages...</p>
        </div>
      ) : currentPackages.length === 0 && (
        <Card className="p-8">
          <p className="text-center text-muted-foreground">
            {searchTerm ? 'No packages found matching your search.' : 'No packages found. Create a new package to get started.'}
          </p>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Package"
        description="This will permanently delete the package from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default PackagesList;